/**
 * SPH Heatmap Computation and Overlay Rendering
 *
 * Compute shader that generates per-particle heatmap colors based on
 * various physical properties: velocity, pressure, temperature, density,
 * age, source ID, turbulence, and fill order.
 */

export const HEATMAP_COMPUTE = /* wgsl */`
struct SPHParams {
  particleCount: u32,
  maxParticles: u32,
  smoothingRadius: f32,
  restDensity: f32,
  stiffness: f32,
  viscosity: f32,
  gravity: vec3<f32>,
  timeStep: f32,
  damping: f32,
  surfaceTension: f32,
  restitution: f32,
  heatmapTipi: u32, // 0=velocity, 1=pressure, 2=temp, 3=density, 4=age, 5=source, 6=turbulence, 7=fillOrder
  minHeatmapValue: f32,
  maxHeatmapValue: f32,
  _pad0: f32,
  _pad1: f32,
}

@group(0) @binding(0) var<uniform> params: SPHParams;
@group(0) @binding(1) var<storage, read> positionMass: array<vec4<f32>>;
@group(0) @binding(2) var<storage, read> velocityAge: array<vec4<f32>>;
@group(0) @binding(3) var<storage, read> densityMisc: array<vec4<f32>>; // x=density, y=pressure, z=temperature, w=sourceId
@group(0) @binding(4) var<storage, read_write> heatmapColors: array<u32>; // Packed RGBA8 (0xAABBGGRR)

// ============================================================================
// COLORMAP FUNCTIONS
// ============================================================================

// Jet colormap (blue -> cyan -> green -> yellow -> red)
fn colormapJet(t: f32) -> vec3<f32> {
  let t_clamped = clamp(t, 0.0, 1.0);
  var r = 0.0;
  var g = 0.0;
  var b = 0.0;

  if (t_clamped < 0.125) {
    b = 0.5 + 0.5 * (t_clamped / 0.125);
  } else if (t_clamped < 0.375) {
    b = 1.0;
    g = (t_clamped - 0.125) / 0.25;
  } else if (t_clamped < 0.625) {
    g = 1.0;
    b = 1.0 - (t_clamped - 0.375) / 0.25;
    r = (t_clamped - 0.375) / 0.25;
  } else if (t_clamped < 0.875) {
    r = 1.0;
    g = 1.0 - (t_clamped - 0.625) / 0.25;
  } else {
    r = 1.0 - 0.5 * (t_clamped - 0.875) / 0.125;
  }

  return vec3<f32>(r, g, b);
}

// Thermal colormap (blue -> cyan -> green -> yellow -> red)
fn colormapThermal(t: f32) -> vec3<f32> {
  let t_clamped = clamp(t, 0.0, 1.0);
  if (t_clamped < 0.25) {
    let u = t_clamped * 4.0;
    return mix(vec3<f32>(0.0, 0.0, 1.0), vec3<f32>(0.0, 1.0, 1.0), u);
  } else if (t_clamped < 0.5) {
    let u = (t_clamped - 0.25) * 4.0;
    return mix(vec3<f32>(0.0, 1.0, 1.0), vec3<f32>(0.0, 1.0, 0.0), u);
  } else if (t_clamped < 0.75) {
    let u = (t_clamped - 0.5) * 4.0;
    return mix(vec3<f32>(0.0, 1.0, 0.0), vec3<f32>(1.0, 1.0, 0.0), u);
  } else {
    let u = (t_clamped - 0.75) * 4.0;
    return mix(vec3<f32>(1.0, 1.0, 0.0), vec3<f32>(1.0, 0.0, 0.0), u);
  }
}

// Viridis colormap (perceptually uniform)
fn colormapViridis(t: f32) -> vec3<f32> {
  let c0 = vec3<f32>(0.267004, 0.004874, 0.329415);
  let c1 = vec3<f32>(0.282623, 0.140926, 0.457517);
  let c2 = vec3<f32>(0.253935, 0.265254, 0.529983);
  let c3 = vec3<f32>(0.206756, 0.371758, 0.553117);
  let c4 = vec3<f32>(0.163625, 0.471133, 0.558148);
  let c5 = vec3<f32>(0.127568, 0.566949, 0.550556);
  let c6 = vec3<f32>(0.134692, 0.658636, 0.517649);
  let c7 = vec3<f32>(0.266941, 0.748751, 0.440573);
  let c8 = vec3<f32>(0.477504, 0.821444, 0.318195);
  let c9 = vec3<f32>(0.741388, 0.873449, 0.149561);
  let c10 = vec3<f32>(0.993248, 0.906157, 0.143936);

  let t_clamped = clamp(t, 0.0, 1.0);
  let segment = t_clamped * 10.0;
  let idx = floor(segment);
  let frac = segment - idx;

  if (idx < 1.0) { return mix(c0, c1, frac); }
  if (idx < 2.0) { return mix(c1, c2, frac); }
  if (idx < 3.0) { return mix(c2, c3, frac); }
  if (idx < 4.0) { return mix(c3, c4, frac); }
  if (idx < 5.0) { return mix(c4, c5, frac); }
  if (idx < 6.0) { return mix(c5, c6, frac); }
  if (idx < 7.0) { return mix(c6, c7, frac); }
  if (idx < 8.0) { return mix(c7, c8, frac); }
  if (idx < 9.0) { return mix(c8, c9, frac); }
  return mix(c9, c10, frac);
}

// Cool-Warm colormap (diverging: blue -> white -> red)
fn colormapCoolWarm(t: f32) -> vec3<f32> {
  let t_clamped = clamp(t, 0.0, 1.0);
  if (t_clamped < 0.5) {
    let u = t_clamped * 2.0;
    return mix(vec3<f32>(0.0, 0.0, 1.0), vec3<f32>(1.0, 1.0, 1.0), u);
  } else {
    let u = (t_clamped - 0.5) * 2.0;
    return mix(vec3<f32>(1.0, 1.0, 1.0), vec3<f32>(1.0, 0.0, 0.0), u);
  }
}

// HSV to RGB conversion
fn hsv2rgb(hsv: vec3<f32>) -> vec3<f32> {
  let h = hsv.x * 6.0;
  let s = hsv.y;
  let v = hsv.z;
  let c = v * s;
  let x = c * (1.0 - abs((h % 2.0) - 1.0));
  let m = v - c;

  var rgb = vec3<f32>(0.0);
  if (h < 1.0) { rgb = vec3<f32>(c, x, 0.0); }
  else if (h < 2.0) { rgb = vec3<f32>(x, c, 0.0); }
  else if (h < 3.0) { rgb = vec3<f32>(0.0, c, x); }
  else if (h < 4.0) { rgb = vec3<f32>(0.0, x, c); }
  else if (h < 5.0) { rgb = vec3<f32>(x, 0.0, c); }
  else { rgb = vec3<f32>(c, 0.0, x); }

  return rgb + m;
}

// Pack RGB color to RGBA8 (0xAABBGGRR format)
fn packColor(color: vec3<f32>) -> u32 {
  let r = u32(clamp(color.r, 0.0, 1.0) * 255.0);
  let g = u32(clamp(color.g, 0.0, 1.0) * 255.0);
  let b = u32(clamp(color.b, 0.0, 1.0) * 255.0);
  let a = 255u;

  return (a << 24u) | (b << 16u) | (g << 8u) | r;
}

// ============================================================================
// HEATMAP COMPUTATION
// ============================================================================

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) globalId: vec3<u32>) {
  let idx = globalId.x;

  if (idx >= params.particleCount) {
    return;
  }

  // Load particle data
  let pos = positionMass[idx];
  let vel = velocityAge[idx];
  let misc = densityMisc[idx];

  let velocity = vel.xyz;
  let age = vel.w;
  let density = misc.x;
  let pressure = misc.y;
  let temperature = misc.z;
  let sourceId = u32(misc.w);

  var value = 0.0;
  var color = vec3<f32>(1.0, 1.0, 1.0);

  // Compute heatmap value based on mode
  switch (params.heatmapTipi) {
    case 0u: { // Velocity magnitude
      value = length(velocity);
      let t = clamp((value - params.minHeatmapValue) / (params.maxHeatmapValue - params.minHeatmapValue), 0.0, 1.0);
      color = colormapThermal(t);
    }
    case 1u: { // Pressure
      value = pressure;
      let t = clamp((value - params.minHeatmapValue) / (params.maxHeatmapValue - params.minHeatmapValue), 0.0, 1.0);
      color = colormapViridis(t);
    }
    case 2u: { // Temperature
      value = temperature;
      let t = clamp((value - params.minHeatmapValue) / (params.maxHeatmapValue - params.minHeatmapValue), 0.0, 1.0);
      color = colormapThermal(t);
    }
    case 3u: { // Density
      value = density;
      let t = clamp((value - params.minHeatmapValue) / (params.maxHeatmapValue - params.minHeatmapValue), 0.0, 1.0);
      color = colormapViridis(t);
    }
    case 4u: { // Age
      value = age;
      let maxAge = max(params.maxHeatmapValue, 1.0);
      let t = clamp(age / maxAge, 0.0, 1.0);
      color = mix(vec3<f32>(0.0, 1.0, 0.0), vec3<f32>(1.0, 0.0, 0.0), t);
    }
    case 5u: { // Source ID
      let hue = f32(sourceId % 12u) / 12.0;
      color = hsv2rgb(vec3<f32>(hue, 0.85, 0.95));
    }
    case 6u: { // Turbulence (approximate using velocity variance)
      // Estimate turbulence by checking velocity magnitude fluctuation
      // For simplicity, we use velocity magnitude as proxy
      // In a real implementation, you'd compute vorticity or velocity curl
      let speed = length(velocity);

      // Approximate turbulence: high speed = high turbulence
      // Better approach: compute velocity curl from neighboring particles
      let turbulence = speed / max(params.maxHeatmapValue, 1.0);
      let t = clamp(turbulence, 0.0, 1.0);

      // Blue (laminar) to red (turbulent)
      color = colormapJet(t);
    }
    case 7u: { // Fill order (time-based)
      // Assuming particles are added in order, use index as proxy for fill time
      // Or use age if particles are spawned at different times
      let fillTime = age; // Or could use: f32(idx) / f32(params.particleCount)
      let t = clamp(fillTime / max(params.maxHeatmapValue, 1.0), 0.0, 1.0);

      // Blue (first) to red (last)
      color = colormapCoolWarm(t);
    }
    default: {
      color = vec3<f32>(0.5, 0.5, 0.5);
    }
  }

  // Pack and store color
  heatmapColors[idx] = packColor(color);
}
`;

// ============================================================================
// HEATMAP OVERLAY RENDERING (Optional: for 2D overlay display)
// ============================================================================

export const HEATMAP_OVERLAY_VERTEX = /* wgsl */`
struct VertexOutput {
  @builtin(position) position: vec4<f32>,
  @location(0) color: vec4<f32>,
}

struct OverlayParams {
  viewProjMatrix: mat4x4<f32>,
  pointSize: f32,
  _pad0: f32,
  _pad1: f32,
  _pad2: f32,
}

@group(0) @binding(0) var<uniform> params: OverlayParams;
@group(0) @binding(1) var<storage, read> positionMass: array<vec4<f32>>;
@group(0) @binding(2) var<storage, read> heatmapColors: array<u32>;

// Unpack RGBA8 color
fn unpackColor(packed: u32) -> vec4<f32> {
  let r = f32(packed & 0xFFu) / 255.0;
  let g = f32((packed >> 8u) & 0xFFu) / 255.0;
  let b = f32((packed >> 16u) & 0xFFu) / 255.0;
  let a = f32((packed >> 24u) & 0xFFu) / 255.0;
  return vec4<f32>(r, g, b, a);
}

@vertex
fn main(
  @builtin(vertex_index) vertexIndex: u32,
  @builtin(instance_index) instanceIndex: u32
) -> VertexOutput {
  var output: VertexOutput;

  // Get particle position
  let pos = positionMass[instanceIndex];
  let worldPos = pos.xyz;

  // Transform to clip space
  output.position = params.viewProjMatrix * vec4<f32>(worldPos, 1.0);

  // Get heatmap color
  output.color = unpackColor(heatmapColors[instanceIndex]);

  return output;
}
`;

export const HEATMAP_OVERLAY_FRAGMENT = /* wgsl */`
struct FragmentInput {
  @location(0) color: vec4<f32>,
}

@fragment
fn main(input: FragmentInput) -> @location(0) vec4<f32> {
  return input.color;
}
`;

// ============================================================================
// HEATMAP LEGEND RENDERING (2D UI overlay)
// ============================================================================

export const HEATMAP_LEGEND_VERTEX = /* wgsl */`
struct VertexOutput {
  @builtin(position) position: vec4<f32>,
  @location(0) uv: vec2<f32>,
}

@vertex
fn main(@builtin(vertex_index) vertexIndex: u32) -> VertexOutput {
  var output: VertexOutput;

  // Generate quad vertices (0,0) to (1,1) in NDC
  let x = f32(vertexIndex & 1u);
  let y = f32((vertexIndex >> 1u) & 1u);

  output.position = vec4<f32>(x * 2.0 - 1.0, y * 2.0 - 1.0, 0.0, 1.0);
  output.uv = vec2<f32>(x, y);

  return output;
}
`;

export const HEATMAP_LEGEND_FRAGMENT = /* wgsl */`
struct FragmentInput {
  @location(0) uv: vec2<f32>,
}

struct LegendParams {
  colormapType: u32, // 0=viridis, 1=jet, 2=thermal, 3=coolwarm
  minValue: f32,
  maxValue: f32,
  orientation: u32, // 0=horizontal, 1=vertical
}

@group(0) @binding(0) var<uniform> params: LegendParams;

// Reuse colormap functions (copied from above for standalone use)
fn colormapViridis(t: f32) -> vec3<f32> {
  let c0 = vec3<f32>(0.267004, 0.004874, 0.329415);
  let c1 = vec3<f32>(0.282623, 0.140926, 0.457517);
  let c2 = vec3<f32>(0.253935, 0.265254, 0.529983);
  let c3 = vec3<f32>(0.206756, 0.371758, 0.553117);
  let c4 = vec3<f32>(0.163625, 0.471133, 0.558148);
  let c5 = vec3<f32>(0.127568, 0.566949, 0.550556);
  let c6 = vec3<f32>(0.134692, 0.658636, 0.517649);
  let c7 = vec3<f32>(0.266941, 0.748751, 0.440573);
  let c8 = vec3<f32>(0.477504, 0.821444, 0.318195);
  let c9 = vec3<f32>(0.741388, 0.873449, 0.149561);
  let c10 = vec3<f32>(0.993248, 0.906157, 0.143936);

  let t_clamped = clamp(t, 0.0, 1.0);
  let segment = t_clamped * 10.0;
  let idx = floor(segment);
  let frac = segment - idx;

  if (idx < 1.0) { return mix(c0, c1, frac); }
  if (idx < 2.0) { return mix(c1, c2, frac); }
  if (idx < 3.0) { return mix(c2, c3, frac); }
  if (idx < 4.0) { return mix(c3, c4, frac); }
  if (idx < 5.0) { return mix(c4, c5, frac); }
  if (idx < 6.0) { return mix(c5, c6, frac); }
  if (idx < 7.0) { return mix(c6, c7, frac); }
  if (idx < 8.0) { return mix(c7, c8, frac); }
  if (idx < 9.0) { return mix(c8, c9, frac); }
  return mix(c9, c10, frac);
}

fn colormapJet(t: f32) -> vec3<f32> {
  let t_clamped = clamp(t, 0.0, 1.0);
  var r = 0.0;
  var g = 0.0;
  var b = 0.0;

  if (t_clamped < 0.125) {
    b = 0.5 + 0.5 * (t_clamped / 0.125);
  } else if (t_clamped < 0.375) {
    b = 1.0;
    g = (t_clamped - 0.125) / 0.25;
  } else if (t_clamped < 0.625) {
    g = 1.0;
    b = 1.0 - (t_clamped - 0.375) / 0.25;
    r = (t_clamped - 0.375) / 0.25;
  } else if (t_clamped < 0.875) {
    r = 1.0;
    g = 1.0 - (t_clamped - 0.625) / 0.25;
  } else {
    r = 1.0 - 0.5 * (t_clamped - 0.875) / 0.125;
  }

  return vec3<f32>(r, g, b);
}

fn colormapThermal(t: f32) -> vec3<f32> {
  let t_clamped = clamp(t, 0.0, 1.0);
  if (t_clamped < 0.25) {
    let u = t_clamped * 4.0;
    return mix(vec3<f32>(0.0, 0.0, 1.0), vec3<f32>(0.0, 1.0, 1.0), u);
  } else if (t_clamped < 0.5) {
    let u = (t_clamped - 0.25) * 4.0;
    return mix(vec3<f32>(0.0, 1.0, 1.0), vec3<f32>(0.0, 1.0, 0.0), u);
  } else if (t_clamped < 0.75) {
    let u = (t_clamped - 0.5) * 4.0;
    return mix(vec3<f32>(0.0, 1.0, 0.0), vec3<f32>(1.0, 1.0, 0.0), u);
  } else {
    let u = (t_clamped - 0.75) * 4.0;
    return mix(vec3<f32>(1.0, 1.0, 0.0), vec3<f32>(1.0, 0.0, 0.0), u);
  }
}

fn colormapCoolWarm(t: f32) -> vec3<f32> {
  let t_clamped = clamp(t, 0.0, 1.0);
  if (t_clamped < 0.5) {
    let u = t_clamped * 2.0;
    return mix(vec3<f32>(0.0, 0.0, 1.0), vec3<f32>(1.0, 1.0, 1.0), u);
  } else {
    let u = (t_clamped - 0.5) * 2.0;
    return mix(vec3<f32>(1.0, 1.0, 1.0), vec3<f32>(1.0, 0.0, 0.0), u);
  }
}

@fragment
fn main(input: FragmentInput) -> @location(0) vec4<f32> {
  // Determine gradient direction
  var t = 0.0;
  if (params.orientation == 0u) {
    t = input.uv.x; // Horizontal
  } else {
    t = 1.0 - input.uv.y; // Vertical (bottom to top)
  }

  // Select colormap
  var color = vec3<f32>(0.0);
  switch (params.colormapType) {
    case 0u: { color = colormapViridis(t); }
    case 1u: { color = colormapJet(t); }
    case 2u: { color = colormapThermal(t); }
    case 3u: { color = colormapCoolWarm(t); }
    default: { color = colormapViridis(t); }
  }

  return vec4<f32>(color, 1.0);
}
`;
